<template>
  <section
    v-if="members.length > 0"
    class="team-module-component">
    <div class="inner-container">
      <div class="tabs">
        <div
          class="tablist desktop"
          role="tablist"
          aria-label="Team Category Tabs">
          <button
            v-for="(tab, idx) in members"
            :id="`tab-${idx}`"
            :key="`tab-${idx}`"
            class="tab"
            :class="{ active: idx === activeTab }"
            role="tab"
            :aria-selected="idx === activeTab ? true : false"
            :aria-controls="`panel-${idx}`"
            tabindex="0"
            @click="selectTab(idx)">
            {{ tab.name }}
          </button>
        </div>
        <div
          class="tablist mobile"
          role="tablist"
          aria-label="Team Category Tabs">
          <div
            class="tab label"
            :class="`label-tab-${activeTab}`"
            @click="slideUpDown()">
            <span>{{ members[activeTab].name }}</span>
            <img
              class="dropdown-arrow"
              :class="{ 'is-open': show }"
              src="@/assets/img/circle_arrow.svg" />
          </div>
          <Vue3SlideUpDown
            v-model="show"
            class="dropdown">
            <button
              v-for="(tab, idx) in members"
              :id="`tab-${idx}`"
              :key="`tab-${idx}`"
              class="tab"
              :class="{ active: idx === activeTab }"
              role="tab"
              :aria-selected="idx === activeTab ? true : false"
              :aria-controls="`panel-${idx}`"
              tabindex="0"
              @click="selectTab(idx, true)">
              {{ tab.name }}
            </button>
          </Vue3SlideUpDown>
        </div>
        <div
          v-for="(tab, idx) in members"
          :id="`panel-${idx}`"
          :key="`panel-${idx}`"
          role="tabpanel"
          tabindex="0"
          :aria-labelledby="`tab-${idx}`"
          class="team-tabpanel"
          :class="{ active: idx === activeTab }">
          <a
            v-for="(member, member_idx) in tab.members"
            :key="`member-${member_idx}`"
            :href="member.url"
            class="member">
            <div class="member-top">
              <img
                class="member-top-img"
                :src="
                  member.featured_image
                    ? member.featured_image.full.url
                    : thumb_placeholder
                "
                :alt="`Photo of ${member.name}`" />
              <div class="member-top-deco" />
            </div>
            <div class="member-bottom">
              <span class="member-bottom-name">{{ member.post_title }}</span>
              <span
                class="member-bottom-title"
                v-html="Array.isArray(member.acf) ? null : member.acf.title" />
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "TeamComponent",
  "CLASSNAME": "team-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */

import { onBeforeMount, ref } from 'vue'
import thumb_placeholder from '@/assets/img/no-thumbnail.jpg'
import { Vue3SlideUpDown } from 'vue3-slide-up-down'

const activeTab = ref(0)
const members = ref([])
const show = ref(false)
const selectTab = (idx, isDropDown = false) => {
  activeTab.value = idx

  if (isDropDown) {
    slideUpDown()
  }
}

const slideUpDown = () => {
  show.value = !show.value
}

const props = defineProps(['data'])

const formatCategoryName = (name) => {
  return name.replace('_tab', '').replaceAll('_', ' ')
}

const formatMembers = () => {
  const data = props.data
  const values = Object.values(data)
  const keys = Object.keys(data)
  values.shift()
  keys.shift()
  keys.forEach((key, idx) => {
    members.value.push({
      name: formatCategoryName(key),
      members: values[idx],
    })
  })
}

onBeforeMount(() => {
  formatMembers()
})
</script>

<style lang="scss">
.team-module-component {
  @apply py-[50px];
  --first-gradient: linear-gradient(90deg, #99b4ff 0%, #6fcfd7 100%);
  --second-gradient: linear-gradient(90deg, #6fcfd7 0%, #ffad39 100%);
  --third-gradient: linear-gradient(90deg, #ffad39 0%, #ff6d00 100%);
  --second-gradient-mobile: linear-gradient(
      0deg,
      rgba(243, 240, 237, 0.8) 0%,
      rgba(243, 240, 237, 0.8) 100%
    ),
    linear-gradient(90deg, #6fcfd7 0%, #ffad39 100%);
  --third-gradient-mobile: linear-gradient(
      0deg,
      rgba(243, 240, 237, 0.8) 0%,
      rgba(243, 240, 237, 0.8) 100%
    ),
    linear-gradient(90deg, #ffad39 0%, #ff6d00 100%);

  .inner-container {
    @apply px-4 lg:px-[75px];

    .tabs {
      @apply flex flex-col items-center;

      .tablist {
        &.desktop {
          @apply hidden lg:flex flex-col items-center large mb-[71px] w-full lg:flex-row lg:justify-center;

          .tab {
            @apply capitalize relative py-[21px] ml-[1px] outline outline-1 outline-lm_lavender w-full max-w-[400px] lg:max-w-[360px];

            &::before {
              @apply opacity-0 absolute content-[''] w-full h-full z-[-1] bg-[red] left-0 top-0 transition-all duration-300;
              background: var(--first-gradient);
            }

            &:nth-child(2) {
              &::before {
                background: var(--second-gradient);
              }
            }

            &:nth-child(3) {
              &::before {
                background: var(--third-gradient);
              }
            }

            &.active {
              &::before {
                @apply opacity-100;
              }
            }
          }
        }

        &.mobile {
          @apply relative w-full mb-5 lg:hidden;

          .tab {
            @apply capitalize flex justify-between items-center large pl-[26px] pr-[18px] relative mt-[1px] outline outline-1 outline-lm_lavender h-[70px] w-full;

            &::before {
              @apply opacity-100 absolute content-[''] w-full h-full z-[-1] bg-[red] left-0 top-0 transition-all duration-300;
              background: var(--first-gradient);
            }

            &:nth-child(2) {
              &::before {
                background: var(--second-gradient-mobile);
              }
            }

            &:nth-child(3) {
              &::before {
                background: var(--third-gradient-mobile);
              }
            }

            &.active {
              @apply hidden;
            }

            &.label {
              .dropdown-arrow {
                @apply rotate-180 transition-all duration-300;

                &.is-open {
                  @apply rotate-0;
                }
              }
            }

            &.label-tab-0 {
              &::before {
                background: var(--first-gradient);
              }
            }

            &.label-tab-1 {
              &::before {
                background: var(--second-gradient-mobile);
              }
            }

            &.label-tab-2 {
              &::before {
                background: var(--third-gradient-mobile);
              }
            }
          }

          .dropdown {
            @apply absolute z-1 w-full;
          }
        }
      }

      .team-tabpanel {
        @apply hidden w-full flex-row justify-start flex-wrap gap-4 lg:gap-5 xl:w-[1080px];

        &.active {
          @apply flex;
        }

        .member {
          @apply flex flex-col gap-3 w-[calc(50%_-_8px)] min-h-[260px] md:min-h-[335px] md:w-[calc(33.3%_-_13.2px)] lg:md:w-[calc(25%_-_15px)] xl:w-[255px];

          &-top {
            &-img {
              @apply w-full h-[200px] object-cover md:h-[255px];
            }

            &-deco {
              @apply w-full h-[13px];
              background: var(--third-gradient);
            }
          }

          &-bottom {
            @apply flex flex-col;

            &-name {
              @apply text-lg not-italic font-normal leading-[22.742px] tracking-[0.179px] lg:large;
            }

            &-title {
              @apply text-[14.5px] not-italic font-normal leading-[19.5px] lg:small;
            }
          }
        }

        &:nth-child(3) {
          .member {
            &-top {
              &-deco {
                background: var(--first-gradient);
              }
            }
          }
        }

        &:nth-child(4) {
          .member {
            &-top {
              &-deco {
                background: var(--second-gradient);
              }
            }
          }
        }
      }
    }
  }
}
</style>
